import React, { useState, useEffect, useRef, useCallback } from "react";

import {
  ChevronRight,
  FileText,
} from "lucide-react";
import { pageRootTestIds } from "../../testing/pageRootTestIds";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useLocation } from "react-router-dom";

import CfLoader from "../../common/CfLoader/CfLoader";
import SmallCFLoader from "../../common/SmallCFLoader/SmallCFLoader";
import Toast from "../../common/Toast/Toast";
import EventCard from "../../components/AuditLogs/EventCard";
import EntityConstants from "../../configs/EntityConstants";
import { GET } from "../../service/CRUDService";

const LIMIT = 5;

const AuditLogs = () => {
  const { t } = useTranslation();
  const { transaction_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [events, setEvents] = useState([]);
  const [verificationId, setVerificationId] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const sentinelRef = useRef(null);
  const isFetchingRef = useRef(false);

  const fetchPage = useCallback(
    async (currentOffset) => {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;
      try {
        const response = await GET(
          `${EntityConstants.TRANSACTION_AUDIT_LOGS}/${transaction_id}/logs?limit=${LIMIT}&offset=${currentOffset}`,
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch audit logs: ${response.status}`);
        }

        const data = await response.json();

        const incoming = Array.isArray(data?.results) ? data.results : [];
        const total = data?.count ?? incoming.length;

        if (currentOffset === 0) {
          setVerificationId(data?.verification_logs_id || transaction_id);
          setTotalCount(total);
          setEvents(incoming);
        } else {
          setEvents((prev) => [...prev, ...incoming]);
        }

        const nextOffset = currentOffset + incoming.length;
        setOffset(nextOffset);
        setHasMore(data?.next != null || nextOffset < total);
      } catch {
        Toast({ type: "error", title: t("auditLogs:error"), message: t("auditLogs:failed_load_audit_logs") });
      } finally {
        isFetchingRef.current = false;
        setInitialLoading(false);
        setLoadingMore(false);
      }
    },
    [transaction_id, t],
  );

  useEffect(() => {
    setInitialLoading(true);
    fetchPage(0);
  }, [fetchPage]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isFetchingRef.current) {
          setLoadingMore(true);
          fetchPage(offset);
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, offset, fetchPage]);

  const handleBack = () => {
    navigate(`/issuer/approvals/${transaction_id}`, { state: location.state });
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-cfSecondary flex items-start justify-center" id={pageRootTestIds.auditLogs}>
        <div className="mt-40">
          <CfLoader />
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-cfSecondary pt-20 pb-12"
      id={pageRootTestIds.auditLogs}
    >
      <div className="w-full bg-cfSearchContainer px-4">
        <div className="flex items-center h-[4rem] mx-auto gap-2 text-sm">
          <button
            onClick={handleBack}
            id="audit-logs-breadcrumb-approvals"
            className="flex items-center gap-1 text-gray-500 hover:text-cfHeadingText transition-colors focus:outline-none"
          >
            {t("auditLogs:verification_approvals")}
          </button>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <button
            onClick={handleBack}
            id="audit-logs-breadcrumb-transaction"
            className="text-gray-500 hover:text-cfHeadingText transition-colors focus:outline-none"
          >
            #{transaction_id}
          </button>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <span className="font-semibold text-cfHeadingText">{t("auditLogs:audit_log")}</span>
        </div>
      </div>

      <div className="mx-auto px-4 mt-8">
        {/* Page header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-cfHeadingText">
            {t("auditLogs:transaction_audit_log")}
          </h1>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <span className="text-sm text-gray-500">{t("auditLogs:verification_id")}</span>
            <span className="text-sm font-bold px-2 py-0.5 rounded-md text-cfPrimary bg-cfDisabledLight">
              {verificationId}
            </span>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full border border-gray-200 bg-gray-50 text-gray-500">
              {totalCount} {t("auditLogs:events")}
            </span>
          </div>
        </div>

        {/* Timeline */}
        {events.length === 0 ? (
          <div
            id="audit-logs-empty-state"
            className="flex flex-col items-center justify-center py-24 text-center"
          >
            <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4 bg-cfDisabledLight">
              <FileText className="w-8 h-8 text-cfPrimary" />
            </div>
            <p className="text-lg font-semibold text-gray-700">{t("auditLogs:no_audit_events_found")}</p>
            <p className="text-sm text-gray-500 mt-1">
              {t("auditLogs:no_audit_events_recorded")}
            </p>
          </div>
        ) : (
          <>
            <div>
              {events.map((event, index) => (
                <EventCard
                  key={event.id ?? `${event.event}-${event.created_at}-${index}`}
                  event={event}
                  eventIndex={index}
                  isLast={index === events.length - 1 && !hasMore}
                />
              ))}
            </div>

            {/* Infinite-scroll sentinel */}
            <div ref={sentinelRef} className="py-4 flex justify-center">
              {loadingMore && <SmallCFLoader />}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AuditLogs;
